import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { SpinnerDialogComponent } from 'src/app/components/dialogs/spinner-dialog/spinner-dialog.component';
import { IExamModel, IExamPageModel, IStudentModel } from 'src/app/components/examMode/interfaces';
import { AsgardDocFroala } from 'src/app/core/documents/asgard-doc-froala';
import { MimeTypesEnum, RequestTypeEnum } from 'src/app/utils/enums';
import { environment } from 'src/environments/environment';
import { AccountService } from '../auth/account.service';
import { AuthService } from '../auth/auth.service';
import { BaseApiService, REQUEST_TYPE_HEADER_KEY } from '../auth/base-api.service';
import { BlobToPdfService } from '../blob-to-pdf.service';
import { DocumentManagerService } from '../document/document-manager.service';
import { CurrentExamService } from '../exam/current-exam.service';
import { Exam } from '../exam/interfaces/exam.interface';
import { HtmlToRtfConversion } from '../file-convertor/conversions';
import { FileConvertorService, FileReadyEvent } from '../file-convertor/file-convertor-service';
import { ConversionOptions } from '../file-convertor/models/conversion-options';

@Injectable({
  providedIn: 'root'
})
export class ExamService extends BaseApiService {
  private get baseUrl(): string {
    return environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland;
  }

  constructor(
    public http: HttpClient,
    private authService: AuthService,
    private accountService: AccountService,
    private currentExamService: CurrentExamService,
    private readonly fileConvertor: FileConvertorService,
    private readonly documentManager: DocumentManagerService,
    private readonly blobToPdfService: BlobToPdfService,
    private readonly dialog: MatDialog,
  ) {
    super(http);
  }

  public getExamsList(filter = '', sortColumn = '', sortOrder = 'asc', showAllSupervisors = false,
    pageNumber = 0, pageSize = 5): Observable<IExamPageModel> {
    const url = this.baseUrl + '/exam/GetExamsList';
    return this.http.get(url, {
      headers: new HttpHeaders().append(REQUEST_TYPE_HEADER_KEY, RequestTypeEnum.ExamList),
      params: new HttpParams()
        .set('sortColumn', sortColumn)
        .set('filter', filter)
        .set('sortOrder', sortOrder)
        .set('pageNumber', pageNumber.toString())
        .set('pageSize', pageSize.toString())
        .set('showAllSupervisors', showAllSupervisors)
    }).pipe(map((result: IExamPageModel) => this.sanitizeObject(result))) as Observable<IExamPageModel>;
  }

  async getStudents(examId: number): Promise<IStudentModel[] | null> {
    const url = this.baseUrl + `/exam/GetStudents?examId=${examId}`;

    return this.get<IStudentModel[]>(url, undefined, RequestTypeEnum.ExamStudents);
  }

  async getExamById(id: number): Promise<IExamModel | null> {
    const url = this.baseUrl + '/exam/GetExam/' + id;

    return this.get<IExamModel>(url, undefined, RequestTypeEnum.ExamGetById)
    .then((exam: IExamModel) => this.sanitizeObject(exam));
  }

  async downloadExamFile(examId: number, name: string, path: string): Promise<any> {
    const url = `${this.baseUrl}/examDocuments/GetExamDocuments/${examId}?Name=${encodeURIComponent(name)}&Path=${encodeURIComponent(path)}`;

    return this.get(url, { responseType: 'blob', observe: 'body' }, RequestTypeEnum.ExamDownloadFile);
  }

  async saveExamFiles(): Promise<any> {
    const authInfo = this.authService.getAuthInfo();
    const accountInfo = await this.accountService.getAccountInfo();
    const { examMetadata } = this.currentExamService
    const drafts = await this.getDraftFiles();
    const document = await this.getExamFile();

    if (authInfo) {
      const formData: any = new FormData();
      formData.append('ExamFile', document, document?.name);
      formData.append('StudentInfo.Name', `${accountInfo?.firstName} ${accountInfo?.lastName}`);
      formData.append('StudentInfo.Username', accountInfo?.username);
      formData.append('StudentInfo.Email', accountInfo?.email);
      drafts.forEach(draft => {
        formData.append('Drafts', draft, draft.name);
      });
      formData.append('ExamId', examMetadata?.id);

      const url = this.baseUrl + '/exam/SaveStudentExam';

      return this.post<void>(url, formData, undefined, RequestTypeEnum.ExamSaveFiles);
    }

    return null;
  }

  async submitExam(fromExamInterruptedDialog: boolean = false): Promise<any> {
    const authInfo = this.authService.getAuthInfo();
    const accountInfo = await this.accountService.getAccountInfo();
    const { examMetadata } = this.currentExamService;
    const drafts = await this.getDraftFiles();
    const document = await this.getExamFile();

    if (authInfo) {
      // start spinner
      const processDialog = this.dialog.open(SpinnerDialogComponent, { panelClass: 'spinner-dialog-container' });

      // collect data
      const formData: any = new FormData();
      formData.append('ExamFile', document, document?.name);
      formData.append('StudentInfo.Name', `${accountInfo?.firstName} ${accountInfo?.lastName}`);
      formData.append('StudentInfo.Username', accountInfo?.username);
      formData.append('StudentInfo.Email', accountInfo?.email);
      drafts.forEach(draft => {
        formData.append('Drafts', draft, draft.name);
      });
      formData.append('ExamId', examMetadata?.id);
      formData.append('FromExamInterruptedDialog', fromExamInterruptedDialog);

      const url = this.baseUrl + '/exam/submitExam';

      // upload data
      return this.post<void>(url, formData, undefined, RequestTypeEnum.ExamSubmit)
      .finally(() => {
        
        // close spinner when upload is complete
        processDialog.close();
      });
    }

    return null;
  }

  async extendExam(unlockCode: string, extraMinutes: number): Promise<boolean> {
    const endpoint = environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland + '/Exam/ExtendExam';
    const extensionModel = {
      "examId": this.currentExamService.examMetadata?.id,
      extraMinutes,
      unlockCode
    };

    return new Promise<boolean>(resolve =>
      this.post(endpoint, extensionModel, undefined, RequestTypeEnum.ExamExtend)
        .then(() => resolve(true))
        .catch(() => resolve(false))
    );

  }

  async checkCode(code: string): Promise<boolean> {
    const examInfo = this.currentExamService.examMetadata;
    const endpoint = environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland + '/code/CheckUnlockCode';

    return new Promise<boolean>(resolve => {
      this.post(endpoint, { code: code, examId: examInfo?.id }, undefined, RequestTypeEnum.ExamCheckUnlockCode)
        .then(() => resolve(true))
        .catch(() => resolve(false))
    });
  }

  async getUnlockCodeAttempts(): Promise<number> {
    const examInfo = this.currentExamService.examMetadata;
    const url = this.baseUrl + `/code/GetUnlockCodeAttempts?examId=${examInfo?.id}`;

    return this.get<number>(url, undefined, RequestTypeEnum.ExamCheckAccessCode);

  }
  async checkAccessCode(code: string): Promise<Exam | null> {
    const accountInfo = await this.accountService.getAccountInfo();
    const formData: any = new FormData();
    formData.append('StudentInfo.Name', `${accountInfo?.firstName} ${accountInfo?.lastName}`);
    formData.append('StudentInfo.Username', accountInfo?.username);
    formData.append('StudentInfo.Email', accountInfo?.email);
    formData.append('Code', code);

    const url = this.baseUrl + `/code/connectByAccessCode`;

    return this.post<Exam>(url, formData, undefined, RequestTypeEnum.ExamCheckAccessCode);

  }

  async deleteExam(examId): Promise<void[]> {
    const url = this.baseUrl + `/exam/${examId}`;

    return this.delete<void>(url, undefined, RequestTypeEnum.CancelExam);
  }

  private async getDraftFiles(): Promise<File[]> {
    const draftFiles: File[] = [];

    const { draftTabIds } = this.currentExamService

    for (let i = 0; i < draftTabIds.length; i++) {
      const doc = this.documentManager.getDocumentById(draftTabIds[i]);

      if (doc && doc instanceof AsgardDocFroala) {
        const html = doc.getHtmlContent();

        if (!!html) {
          const fileName = `Draft${i + 1}`;
          const file = new File([html], fileName, { type: MimeTypesEnum.TextHtml });

          const opts: ConversionOptions = {
            file,
            fileName
          };

          this.fileConvertor.silentMakeConversion<HtmlToRtfConversion>(HtmlToRtfConversion, opts);
          const convertedFile = await lastValueFrom<FileReadyEvent>(this.fileConvertor.FileReady.pipe(first()));
          draftFiles.push(convertedFile.file);
        }
      }
    }

    return draftFiles;
  }

  private async getExamFile(): Promise<File> {
    const { examTabId } = this.currentExamService;
    return await this.blobToPdfService.GetPdfFromTabId(examTabId);
  }
}
